// 批次 20（admin 首頁專批）：科部管理員首頁的整頁 body（crud-list 頁 opt-in，dashboardBody 同款）。
//
// 鏡像 legacy（main-dept-admin-main＝/main/departmentAdminMain；main-main 的 admin 分流同頁渲染，
// MainController.java:174-175 getWesternMedicineUGYView isDepartmentAdmin→deptAdminMain）：
//   - deptAdminMain.jsp(130)：menuRow（空，JS 填）＋公告欄（NO./主旨/公告時間＋More/Less）＋
//     #planTargetList＋clinicalArrangementModal（教師名單安排，ClinicalArrangementModal.tsx）
//   - deptAdminMain.js(659)：ready :1-8／calendarBox :16-24／More-Less :25-42／公告 :43-53／
//     todoForm 盒 :55-59／plan-item :63-77／panel × :79-88／planForm→planFormList :90-109／
//     courseForm→classFormList :112-129／clinicalRow→modal :133-149／儲存驗證鏈 :180-200／
//     genPlanPanel :208-224（「此計畫尚未安排學員！」:221）／updatePhaseClinical :226-250／
//     getValidPhaseList :256-302／renderPhaseClinicalTable :348-388／changePlanChecked :403-424／
//     queryPlanInfo :426-450／renderDeptAdminPage :453-464／renderPhaseList :466-510／
//     renderCalendarMenu :512-531／renderMenu :553-581（switch 只有 ALL_FORM :559）／
//     scrollToCurrentPhase :584-610／getPhaseClinicalList :632-649
//   - 資料端點群：main/ugy/deptAdmin/getData／plan／clinicalList＋ajax/getDepartmentListByJobTypeAjax
//     ＋ajax/getAccountListByDepartmentIdNewAjax（ePortfolio.js:1752-1770）＋
//     training/planInst/updatePhaseClinical——皆不在 endpoint-map（紅線），以 dashboard-store 種子鏡像。
//   - 行事曆／公告／待填表單／個人行程／檢視課程 modal 重用批次 11/19 元件（dashboardParts／
//     dashboardModals）；courseModal 開啟語境＝modal/calendar.jsp:71-86 eventClick（同批次 19）。
//
// 【證據等級】§7 第 3 級（原始碼）。raw-dom admin/home/main-dept-admin-main.html 的 menuRow 為空
// （AJAX 未完成時捕獲）；menuRow 盒群以 deptAdminMain.js＋mainPageTemplate.jsp 模板為基準。

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from '@/components/base/Link';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import {
  dashboardGate,
  readDashboardData,
  updateDashboardData,
  type DashboardData,
  type DashboardTodoForm,
  type DeptAdminPhase,
  type DeptAdminPlanDto,
} from '@/mocks/dashboard-store';
import { useScenario } from '@/mocks/provider';
import { CourseViewModal } from '@/pages/course/CourseViewModal';
import {
  AnnounceModal,
  ScheduleModal,
  TodoFormModal,
} from '../dashboard/dashboardModals';
import {
  AnnouncementPanel,
  CalendarMenuBox,
  PhaseBox,
  PhaseItem,
  TodoMenuBox,
  kebab,
} from '../dashboard/dashboardParts';
import { LightCalendar } from '../dashboard/LightCalendar';
import {
  ClinicalArrangementModal,
  isValidClinical,
  type DeptAdminPhaseInstSave,
} from './ClinicalArrangementModal';

/** planList_Box_Tmpl 的 {{imgSrc}}（deptAdminMain.js:573）。 */
const PLAN_LIST_IMG_SRC = 'img/main/計畫清單.png';

/** 未來行程過濾（deptAdminMain.js:518-523：start>now 且 eventType!='planInst'；批次 19 同款）。 */
export function futureSchedules(events: DashboardData['calendarEvents'], now: Date) {
  return events.filter((event) => {
    const startDate = new Date(event.start);
    return startDate.getTime() - now.getTime() > 0 && event.eventType !== 'planInst';
  });
}

/** 已開啟的計畫面板狀態（renderPhaseList prepend 語意；同計畫重開＝移除舊面板再 prepend）。 */
interface OpenPanel {
  readonly planTargetId: string;
  readonly dto: DeptAdminPlanDto;
}

/**
 * 計畫面板（mainPageTemplate.jsp:498-541 dept_admin_panel＋renderPhaseList :466-510）：
 * panel-head planTarget.name＋×；titleRow 訓練階段/計畫表單/課程表單/教師安排 4 欄；
 * phaseRow＝phase_Tmpl（PhaseItem，ugy 顯示 phaseDeptName）＋三個 boxRow。
 */
function DeptAdminPanel({
  pageId,
  dto,
  onClose,
  onPlanFormClick,
  onCourseFormClick,
  onClinicalClick,
}: {
  readonly pageId: string;
  readonly dto: DeptAdminPlanDto;
  readonly onClose: () => void;
  readonly onPlanFormClick: (phase: DeptAdminPhase) => void;
  readonly onCourseFormClick: (phase: DeptAdminPhase) => void;
  readonly onClinicalClick: (phase: DeptAdminPhase) => void;
}): ReactElement {
  const panelKey = kebab(dto.planTarget.id);
  const phaseRowRef = useRef<HTMLDivElement | null>(null);

  // scrollToCurrentPhase（deptAdminMain.js:584-610：currentPhase 橫向捲至可見，animate 1000ms；
  // 無 current→第一階段（offsetLeft 差 0＝不動）。雛形同參數以 scrollLeft 直接定位——jsdom 無 animate）。
  useEffect(() => {
    const row = phaseRowRef.current;
    if (row === null) return;
    const current = row.querySelector('[data-is-current="true"]') as HTMLElement | null;
    const first = row.querySelector('li') as HTMLElement | null;
    const target = current ?? first;
    if (target !== null && first !== null) {
      row.scrollLeft = (target.offsetLeft ?? 0) - (first.offsetLeft ?? 0);
    }
  }, [dto]);

  return (
    <ContentPanel
      uiId={`${pageId}-dept-admin-panel-${panelKey}`}
      title={dto.planTarget.name}
      actions={
        <button
          type="button"
          data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-close`}
          title="關閉計畫（close-dept-admin-panel）"
          onClick={onClose}
        >
          ×
        </button>
      }
    >
      <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-plan-row`}>
        <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-title-row`} className="flex text-center text-[13px] font-bold">
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-title-phase`} className="flex-1 p-1">訓練階段</div>
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-title-plan-form`} className="flex-1 p-1">計畫表單</div>
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-title-course-form`} className="flex-1 p-1">課程表單</div>
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-title-clinical`} className="flex-1 p-1">教師安排</div>
        </div>
        <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-boxs-row`} className="flex overflow-x-auto">
          <div ref={phaseRowRef} data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-phase-row`} className="flex">
            <ul className="flex list-none p-0">
              {dto.phaseList.map((phase, index) => (
                <PhaseItem
                  key={phase.phaseTargetId}
                  pageId={`${pageId}-dept-admin-panel-${panelKey}`}
                  index={index}
                  phase={phase}
                />
              ))}
            </ul>
          </div>
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-planform-row`} className="flex">
            {dto.phaseList.map((phase, index) => (
              <PhaseBox
                key={phase.phaseTargetId}
                pageId={`${pageId}-dept-admin-panel-${panelKey}`}
                kind="planForm"
                index={index}
                target={phase.planForm}
                clickable={phase.planForm.totalQuantity > 0}
                onClick={() => onPlanFormClick(phase)}
              />
            ))}
          </div>
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-courseform-row`} className="flex">
            {dto.phaseList.map((phase, index) => (
              <PhaseBox
                key={phase.phaseTargetId}
                pageId={`${pageId}-dept-admin-panel-${panelKey}`}
                kind="classForm"
                index={index}
                target={phase.classAndMeetingForm}
                clickable={phase.classAndMeetingForm.totalQuantity > 0}
                onClick={() => onCourseFormClick(phase)}
              />
            ))}
          </div>
          <div data-ui-id={`${pageId}-dept-admin-panel-${panelKey}-clinical-row`} className="flex">
            {dto.phaseList.map((phase, index) => (
              <PhaseBox
                key={phase.phaseTargetId}
                pageId={`${pageId}-dept-admin-panel-${panelKey}`}
                kind="clinicalArrangement"
                index={index}
                target={phase.clinicalArrangement}
                clickable={phase.clinicalArrangement.totalQuantity > 0}
                onClick={() => onClinicalClick(phase)}
              />
            ))}
          </div>
        </div>
      </div>
    </ContentPanel>
  );
}

export function DeptAdminBody({ pageId }: { pageId: string }): ReactElement {
  const scenario = useScenario();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** 已勾選計畫（changePlanChecked :403-424 的 data-checked 映射）。 */
  const [checkedPlans, setCheckedPlans] = useState<Readonly<Record<string, boolean>>>({});
  /** 已開啟面板（prepend 語意；genPlanPanel :208-224）。 */
  const [openPanels, setOpenPanels] = useState<readonly OpenPanel[]>([]);
  /** 教師名單安排 modal 開啟語境（clinicalRow click :130-146）。 */
  const [clinicalContext, setClinicalContext] = useState<
    { readonly planTarget: DeptAdminPlanDto['planTarget']; readonly phaseData: DeptAdminPhase } | null
  >(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [announceId, setAnnounceId] = useState<string | null>(null);
  const [todoOpen, setTodoOpen] = useState(false);
  const [scheduleEditing, setScheduleEditing] = useState<DashboardData['mySchedules'][number] | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [courseRow, setCourseRow] = useState<DashboardData['courseRows'][number] | null>(null);
  const now = new Date();
  const discussAlertShown = useRef(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        dashboardGate(scenario);
        const { data: merged } = readDashboardData(scenario, now);
        if (alive) {
          setData(merged);
          // 載入提醒（deptAdminMain.jsp:35-38：discussBoardSize>0 → showAlertDialog，整句是連結）。
          if (!discussAlertShown.current && merged.discussBoardSize > 0) {
            discussAlertShown.current = true;
            void dialogs.alert(
              <Link href="/discuss/discussBoard?from=main" uiId={`${pageId}-discuss-board-link`}>
                {`您有${merged.discussBoardSize}則留言,請於留言版確認.`}
              </Link>,
            );
          }
        }
      } catch (error) {
        if (alive) setErrorText(error instanceof Error ? error.message : '查詢失敗');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // now／dialogs 由 render 常數取得（同一次載入語意），不進依賴。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario]);

  /** openUrl 鏡像（deptAdminMain.js :99-127/:136-145 的 openUrl；D20-8 帶參不落地）。deptExtraOptions＝legacy encodeURIComponent 前置（deptAdminMain.js:127），
   // 雛形 URLSearchParams 單層 encode——最終 query 形態等價（批次 19 DashboardBody 同款備註）。 */
  const openUrl = (path: string, params: Record<string, string | number | boolean> = {}) => {
    const search = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)]),
    ).toString();
    navigate(search === '' ? path : `${path}?${search}`);
  };

  if (loading) {
    return <ProcessingOverlay uiId={`${pageId}-loading`} open />;
  }
  if (errorText !== null || data === null) {
    // legacy getDeptAdminData error handler 只 console.log（deptAdminMain.js:541-545）——
    // 雛形以 alert 呈現（DashboardBody 同款），文案＝閘門訊息。
    return (
      <div data-ui-id={`${pageId}-error`} className="p-4">
        {errorText ?? '查詢失敗'}
      </div>
    );
  }

  /**
   * genPlanPanel（deptAdminMain.js:208-224）：phaseList.length>0 → renderPhaseList（先移除同 id
   * 舊面板再 prepend）；else → changePlanChecked 回收＋showAlertDialog('此計畫尚未安排學員！')。
   */
  const genPlanPanel = (planTargetId: string): void => {
    const dto = data.deptAdminPlans[planTargetId];
    if (dto === undefined) {
      // queryPlanInfo error → console.log（deptAdminMain.js:541-545）；雛形以 alert 呈現。
      void dialogs.alert('查詢失敗');
      return;
    }
    if (dto.phaseList.length > 0) {
      setOpenPanels((current) => [
        { planTargetId: dto.planTargetId, dto },
        ...current.filter((panel) => panel.planTargetId !== dto.planTargetId),
      ]);
    } else {
      setCheckedPlans((current) => ({ ...current, [planTargetId]: false }));
      void dialogs.alert('此計畫尚未安排學員！');
    }
  };

  /** plan-item 點擊（deptAdminMain.js:63-77：changePlanChecked；checked→genPlanPanel、取消→移除面板）。 */
  const onPlanItemClick = (planTargetId: string): void => {
    const isCheck = checkedPlans[planTargetId] !== true;
    setCheckedPlans((current) => ({ ...current, [planTargetId]: isCheck }));
    if (isCheck) {
      genPlanPanel(planTargetId);
    } else {
      setOpenPanels((current) => current.filter((panel) => panel.planTargetId !== planTargetId));
    }
  };

  /** 面板 ×（deptAdminMain.js:79-88：移除面板＋changePlanChecked 回收）。 */
  const closePanel = (planTargetId: string): void => {
    setOpenPanels((current) => current.filter((panel) => panel.planTargetId !== planTargetId));
    setCheckedPlans((current) => ({ ...current, [planTargetId]: false }));
  };

  /** 計畫表單點擊（deptAdminMain.js:90-109：planFormList＋planTarget 四參＋phase 三參＋doQueryFlag）。 */
  const onPlanFormClick = (panel: OpenPanel, phase: DeptAdminPhase): void => {
    openUrl('/form/planFormList', {
      defaultAnnualId: panel.dto.planTarget.annualId,
      defaultOrganizationId: panel.dto.planTarget.organizationId,
      defaultJobTypeId: panel.dto.planTarget.jobTypeId,
      defaultPlanTargetId: panel.dto.planTarget.id,
      defaultPhaseDeptId: phase.phaseDeptId,
      defaultStartDate: phase.startDate,
      defaultEndDate: phase.endDate,
      doQueryFlag: 1,
    });
  };

  /** 課程表單點擊（deptAdminMain.js:112-129：classFormList＋defaultDeptId＋deptExtraOptions JSON）。 */
  const onCourseFormClick = (panel: OpenPanel, phase: DeptAdminPhase): void => {
    openUrl('/form/classFormList', {
      defaultOrganizationId: panel.dto.planTarget.organizationId,
      defaultJobTypeId: panel.dto.planTarget.jobTypeId,
      defaultDeptId: phase.phaseDeptId,
      defaultStartDate: phase.startDate,
      defaultEndDate: phase.endDate,
      doQueryFlag: 1,
      deptExtraOptions: JSON.stringify([{ id: phase.phaseDeptId, name: phase.phaseDeptName }]),
    });
  };

  /** 教師安排點擊（deptAdminMain.js:133-149：clinicalAmount／plan-target-id＋clinicalList → modal）。 */
  const onClinicalClick = (panel: OpenPanel, phase: DeptAdminPhase): void => {
    setClinicalContext({ planTarget: panel.dto.planTarget, phaseData: phase });
  };

  /**
   * 儲存（savePhaseInstList :226-250 → training/planInst/updatePhaseClinical :229）：success → modal
   * hide＋genPlanPanel(#plan-target-id) 重抓＋showInfoDialog(resp.desc)。雛形更新 clinicalList 種子
   * ＋重算 clinicalArrangement 盒（assigned/total），面板以新 dto 重新 prepend。
   */
  const onSaveClinical = (payload: readonly DeptAdminPhaseInstSave[]): void => {
    if (clinicalContext === null) return;
    const phaseTargetId = clinicalContext.phaseData.phaseTargetId;
    const planTargetId = clinicalContext.planTarget.id;
    const amount = clinicalContext.planTarget.clinicalAmount;
    const next = updateDashboardData(
      scenario,
      (current) => {
        const rows = current.deptAdminClinicalLists[phaseTargetId] ?? [];
        const savedById = new Map(payload.map((item) => [item.id, item]));
        const updatedRows = rows.map((row) => {
          const saved = savedById.get(row.phaseInstId);
          if (saved === undefined) return row;
          return {
            ...row,
            clinical1Id: amount >= 1 ? Number(saved.clinicalId ?? row.clinical1Id) : row.clinical1Id,
            clinical2Id: amount >= 2 ? Number(saved.clinicalId2 ?? row.clinical2Id) : row.clinical2Id,
            clinical3Id: amount >= 3 ? Number(saved.clinicalId3 ?? row.clinical3Id) : row.clinical3Id,
          };
        });
        // clinicalArrangement 盒重算（assigned＝必填欄全有效的列數；displayText＝「assigned/total」）。
        const assigned = updatedRows.filter((row) => {
          if (amount >= 1 && !isValidClinical(row.clinical1Id)) return false;
          if (amount >= 2 && !isValidClinical(row.clinical2Id)) return false;
          if (amount >= 3 && !isValidClinical(row.clinical3Id)) return false;
          return true;
        }).length;
        const total = updatedRows.length;
        const plan = current.deptAdminPlans[planTargetId];
        const plans =
          plan === undefined
            ? current.deptAdminPlans
            : {
                ...current.deptAdminPlans,
                [planTargetId]: {
                  ...plan,
                  phaseList: plan.phaseList.map((phase) =>
                    phase.phaseTargetId === phaseTargetId
                      ? {
                          ...phase,
                          clinicalArrangement: {
                            completePercent: total > 0 ? (assigned / total) * 100 : 0,
                            totalQuantity: total,
                            displayText: `${assigned}/${total}`,
                          },
                        }
                      : phase,
                  ),
                },
              };
        return {
          ...current,
          deptAdminClinicalLists: { ...current.deptAdminClinicalLists, [phaseTargetId]: updatedRows },
          deptAdminPlans: plans,
        };
      },
      now,
    );
    setClinicalContext(null); // modal hide（:233）
    const refreshed = next.deptAdminPlans[planTargetId];
    if (refreshed !== undefined && refreshed.phaseList.length > 0) {
      // genPlanPanel(#plan-target-id) 重抓（:235-237）：同 id 移除舊面板再 prepend。
      setOpenPanels((currentPanels) => [
        { planTargetId, dto: refreshed },
        ...currentPanels.filter((panel) => panel.planTargetId !== planTargetId),
      ]);
      setCheckedPlans((currentChecked) => ({ ...currentChecked, [planTargetId]: true }));
    }
    void dialogs.info('儲存成功【雛形值待實機校準】'); // showInfoDialog(resp.desc)（:238；desc 為 server 文案）
  };

  /** showFillForm（mainUtils.js:606-616；window.open 鏡像為站內導航——D19-x/D20-8）。 */
  const showFillForm = (formKey: string, refTable: string, refId: string, formTargetId: string) => {
    openUrl('/form/prompt/fillForm', {
      isWindowOpen: 'true',
      fromPage: 'main',
      formKey,
      refTable,
      refId,
      formTargetId,
    });
  };

  /** 待填表單列點擊（modal/todoForm.jsp:90-110 分流；批次 19 openTodoForm 同款）。 */
  const openTodoForm = (row: DashboardTodoForm) => {
    if (row.kind === 'leave') {
      openUrl('/leave/leaveForm', { doQueryFlag: 1, todo: 'true' });
      return;
    }
    if (row.refTable === 'CoEditForm') {
      openUrl('/_/not-implemented');
      return;
    }
    if (row.refTable === 'studyRecord') {
      openUrl('/training/studyRecord/openStudyRecordList');
      return;
    }
    if (row.refTable === 'examList') {
      openUrl('/exam/testing/openExamList');
      return;
    }
    showFillForm(row.formKey, row.refTable, row.refId, row.formTargetId);
  };

  /** 公告點擊（deptAdminMain.js:43-53：showAnnounce＋未讀 span 移除；批次 19 同款）。 */
  const openAnnouncement = (id: string) => {
    setAnnounceId(id);
    const row = data.announcements.find((announcement) => announcement.id === id);
    if (row !== undefined && row.announcementId === undefined) {
      setData(
        updateDashboardData(
          scenario,
          (current) => ({
            ...current,
            announcements: current.announcements.map((announcement) =>
              announcement.id === id ? { ...announcement, announcementId: announcement.id } : announcement,
            ),
          }),
          now,
        ),
      );
    }
  };

  /** 行事曆事件點擊（modal/calendar.jsp:71-86 eventClick；批次 19 同款）。 */
  const onCalendarEventClick = (event: DashboardData['calendarEvents'][number]) => {
    if (event.eventType === 'schedule') {
      // 個人行程 → getMyScheduleAjax（種子明細）→ addCalendarModal（修改個人行程）。
      const detail = data.mySchedules.find((schedule) => schedule.id === event.id) ?? {
        id: event.id,
        content: event.title,
        scheduleStartDateString: event.start.slice(0, 16).replace('T', ' '),
        scheduleEndDateString: event.start.slice(0, 16).replace('T', ' '),
        memo: '',
        groupKey: '',
      };
      setScheduleEditing(detail);
      setScheduleModalOpen(true);
      return;
    }
    if (event.eventType === 'class' || event.eventType === 'meeting') {
      // class|meeting → CourseModal（批次 11 CourseViewModal；種子課程列）。
      const row = data.courseRows.find((candidate) => String(candidate['id']) === event.id);
      if (row === undefined) {
        void dialogs.alert('查無課程資料');
        return;
      }
      setCourseRow(row);
    }
  };

  const dto = data.deptAdminMainDto;
  const schedules = futureSchedules(data.calendarEvents, now);

  return (
    <div data-ui-id={pageId}>
      {/* menuRow（deptAdminMain.jsp:51 div.menuRow；renderMenu :553-581＝todoItemMap 盒（switch 只有
          ALL_FORM→待填表單 clickable；其他 key 照渲染但無 class/圖/文案）＋planList_Box_Tmpl 訓練計畫盒；
          renderCalendarMenu :512-531＝行事曆盒（async 最後 append） */}
      <div data-ui-id={`${pageId}-menu-row`} className="mb-4 flex flex-wrap gap-3">
        {(Object.keys(dto.todoItemMap) as ('ALL_FORM')[]).map((key) => {
          // renderMenu switch（deptAdminMain.js:557-565）：只有 ALL_FORM 有圖/文案/class；其他 key
          // 照渲染但 menuText/imgSrc 為 undefined（＝空盒不可點；種子只種 ALL_FORM，照 renderMenu 形狀）。
          const isAllForm = key === 'ALL_FORM';
          const boxClass = isAllForm ? 'todoForm clickable' : '';
          return (
            <TodoMenuBox
              key={key}
              pageId={pageId}
              boxClass={boxClass}
              imgSrc={isAllForm ? 'img/main/待填表單.png' : ''}
              menuText={isAllForm ? '待填表單' : ''}
              displayText={dto.todoItemMap[key]?.incompleteQuantity ?? 0}
              onClick={isAllForm ? () => setTodoOpen(true) : undefined} // :55-59 queryMyTodoFormList('all')
            />
          );
        })}
        {/* 訓練計畫盒（planList_Box_Tmpl:480-496；iconText 寫死「訓練計畫」；plan-item data-checked） */}
        <div
          data-ui-id={`${pageId}-menu-box-plan-list`}
          className="inline-flex min-w-[260px] items-stretch border border-border"
        >
          <div data-ui-id={`${pageId}-menu-box-plan-list-icon`} className="flex w-[35%] flex-col items-center justify-center p-2">
            <span data-ui-id={`${pageId}-menu-box-plan-list-img`} title={PLAN_LIST_IMG_SRC} className="text-[28px] leading-none">
              ▣
            </span>
            <p className="text-center text-[13px]">訓練計畫</p>
          </div>
          <div data-ui-id={`${pageId}-menu-box-plan-list-data`} className="flex w-[65%] flex-col justify-center gap-1 border-l border-border p-2">
            {dto.planTargetList.map((plan) => {
              const checked = checkedPlans[plan.id] === true;
              return (
                <div
                  key={plan.id}
                  data-ui-id={`${pageId}-plan-item-${kebab(plan.id)}`}
                  data-plan-id={plan.id}
                  data-checked={checked ? 'true' : 'false'}
                  className="cursor-pointer"
                  onClick={() => onPlanItemClick(plan.id)}
                >
                  <div data-ui-id={`${pageId}-plan-item-${kebab(plan.id)}-check`} className="text-[12px] text-[#5cb85c]">
                    {/* changePlanChecked :403-424：fa-check 顯隱＋plan-checked 高亮。 */}
                    <i className={checked ? '' : 'hidden'} aria-hidden="true">
                      ✓
                    </i>
                  </div>
                  <div
                    data-ui-id={`${pageId}-plan-item-${kebab(plan.id)}-name`}
                    className={checked ? 'font-bold text-[#337ab7]' : ''}
                  >
                    {plan.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* 行事曆盒（renderCalendarMenu :512-531；calendarBox click → calendarModal :16-24） */}
        <CalendarMenuBox pageId={pageId} now={now} schedules={schedules} onClick={() => setCalendarOpen(true)} />
      </div>

      {/* 公告欄（deptAdminMain.jsp:53-80；More/Less＝announcesLimit 3，批次 19 同款） */}
      <AnnouncementPanel pageId={pageId} announcements={data.announcements} onOpenAnnouncement={openAnnouncement} />

      {/* planTargetList（#planTargetList；genPlanPanel prepend；renderPhaseList 同 id 移除再 prepend） */}
      <div data-ui-id={`${pageId}-plan-target-list`} className="mt-4 flex flex-col gap-4">
        {openPanels.map((panel) => (
          <DeptAdminPanel
            key={panel.planTargetId}
            pageId={pageId}
            dto={panel.dto}
            onClose={() => closePanel(panel.planTargetId)}
            onPlanFormClick={(phase) => onPlanFormClick(panel, phase)}
            onCourseFormClick={(phase) => onCourseFormClick(panel, phase)}
            onClinicalClick={(phase) => onClinicalClick(panel, phase)}
          />
        ))}
      </div>

      {/* 行事曆 modal（modal/calendar.jsp：calendarBox click → #calendarModal；月曆＝LightCalendar） */}
      <ModalFrame
        uiId={`${pageId}-calendar-modal`}
        open={calendarOpen}
        title="行事曆"
        onClose={() => setCalendarOpen(false)}
        size="lg"
      >
        <LightCalendar uiId={`${pageId}-calendar`} events={data.calendarEvents} onEventClick={onCalendarEventClick} />
      </ModalFrame>

      {/* 公告內容 modal（announcementModal；modal/announce.jsp showAnnounce） */}
      <AnnounceModal
        pageId={pageId}
        open={announceId !== null}
        detail={announceId === null ? null : (data.announcementDetails[announceId] ?? null)}
        onClose={() => setAnnounceId(null)}
      />

      {/* 待填表單 modal（todoFormModal；modal/todoForm.jsp；todoForm 盒 click :55-59 queryMyTodoFormList('all')。
          列點擊分流＝modal/todoForm.jsp:90-110（批次 19 openTodoForm 同款）。 */}
      <TodoFormModal
        pageId={pageId}
        open={todoOpen}
        todoForms={data.todoForms}
        onClose={() => setTodoOpen(false)}
        onOpenForm={openTodoForm}
      />

      {/* 教師名單安排 modal（clinicalArrangementModal；clinicalRow click :130-146）。
          掛 open 條件渲染：內部 useState 以 clinicalRows 初值，重開時必須重 mounting。 */}
      {clinicalContext !== null ? (
        <ClinicalArrangementModal
          pageId={pageId}
          open
          planTarget={clinicalContext.planTarget}
          phaseData={clinicalContext.phaseData}
          clinicalRows={data.deptAdminClinicalLists[clinicalContext.phaseData.phaseTargetId] ?? []}
          departments={data.deptAdminDepartments}
          accounts={data.deptAdminAccounts}
          loginAccountDepartmentId={data.loginAccountDepartmentId}
          onClose={() => setClinicalContext(null)}
          onSave={onSaveClinical}
        />
      ) : null}

      {/* 個人行程 modal（addCalendarModal；eventClick schedule → 修改個人行程；批次 19 同款）。 */}
      {scheduleModalOpen ? (
        <ScheduleModal
          pageId={pageId}
          open={scheduleModalOpen}
          schedule={scheduleEditing}
          onClose={() => setScheduleModalOpen(false)}
          onSave={(values) => {
            setData(
              updateDashboardData(
                scenario,
                (current) => {
                  const exists = current.mySchedules.some((schedule) => schedule.id === values.id);
                  return {
                    ...current,
                    mySchedules: exists
                      ? current.mySchedules.map((schedule) => (schedule.id === values.id ? values : schedule))
                      : [...current.mySchedules, values],
                  };
                },
                now,
              ),
            );
            setScheduleEditing(values); // saveSchedule 成功後標題變「修改個人行程」
            void dialogs.alert('存檔成功【雛形值待實機校準】'); // showInfoDialog(response.desc)
          }}
          onDelete={() => {
            if (scheduleEditing === null) return;
            setData(
              updateDashboardData(
                scenario,
                (current) => ({
                  ...current,
                  mySchedules: current.mySchedules.filter((schedule) => schedule.id !== scheduleEditing.id),
                }),
                now,
              ),
            );
            setScheduleModalOpen(false);
            void dialogs.alert('刪除成功【雛形值待實機校準】');
          }}
          onApprove={(isEnable) => {
            void dialogs.alert(isEnable === 1 ? '已接受【雛形值待實機校準】' : '已婉拒【雛形值待實機校準】');
          }}
        />
      ) : null}

      {/* 檢視課程 modal（courseModal＝批次 11 CourseViewModal；行事曆 class/meeting 點擊） */}
      {courseRow !== null ? (
        <CourseViewModal pageId={pageId} row={courseRow} onClose={() => setCourseRow(null)} />
      ) : null}
    </div>
  );
}
